import { Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider.js';
import { LoginPage } from './auth/LoginPage.js';
import { ProtectedRoute } from './auth/ProtectedRoute.js';
import { AppLayout } from './components/layout/AppLayout.js';
import { CustomersPage } from './features/customers/CustomersPage.js';
import { ContactsPage } from './features/contacts/ContactsPage.js';
import { ProspectsPage } from './features/sales/ProspectsPage.js';
import { CustomerTypesPage } from './features/customer-types/CustomerTypesPage.js';
import { AgreementSubtypesPage } from './features/agreement-subtypes/AgreementSubtypesPage.js';
import { ProjectsPage } from './features/projects/ProjectsPage.js';
import { ProjectDetailPage } from './features/projects/ProjectDetailPage.js';
import { WorkForecastPage } from './features/work-forecast/WorkForecastPage.js';
import { ProductsPage } from './features/products/ProductsPage.js';
import { ServiceCategoriesPage } from './features/service-categories/ServiceCategoriesPage.js';
import { BillingClassesPage } from './features/billing-classes/BillingClassesPage.js';
import { TicketsPage } from './features/tickets/TicketsPage.js';
import { TicketTypesPage } from './features/tickets/TicketTypesPage.js';
import { TicketTypeGroupsPage } from './features/tickets/TicketTypeGroupsPage.js';
import { TicketStatusesPage } from './features/tickets/TicketStatusesPage.js';
import { TimeEntriesPage } from './features/time-entries/TimeEntriesPage.js';
import { TimesheetsPage } from './features/timesheets/TimesheetsPage.js';
import { TimesheetDetailPage } from './features/timesheets/TimesheetDetailPage.js';
import { ApprovalsPage } from './features/timesheets/ApprovalsPage.js';
import { AgreementsPage } from './features/agreements/AgreementsPage.js';
import { AgreementDetailPage } from './features/agreements/AgreementDetailPage.js';
import { OpportunitiesPage } from './features/opportunities/OpportunitiesPage.js';
import { InvoicesPage } from './features/invoices/InvoicesPage.js';
import { GenerateInvoicesPage } from './features/invoices/GenerateInvoicesPage.js';
import { InvoiceDetailPage } from './features/invoices/InvoiceDetailPage.js';
import { QboBatchPage } from './features/invoices/QboBatchPage.js';
import { InvoiceTemplatesPage } from './features/invoice-templates/InvoiceTemplatesPage.js';
import { BillingAuditPage } from './features/billing-audit/BillingAuditPage.js';
import { MembersPage } from './features/members/MembersPage.js';
import { RolesPage } from './features/roles/RolesPage.js';
import { ApiUsersPage } from './features/api-users/ApiUsersPage.js';
import { ExcelAccessPage } from './features/data-feed/ExcelAccessPage.js';
import { ConnectWisePage } from './features/connectwise/ConnectWisePage.js';
import { QboIntegrationPage } from './features/qbo/QboIntegrationPage.js';
import { ReportsPage } from './features/reports/ReportsPage.js';
import { BonusReportPage } from './features/reports/BonusReportPage.js';
import { CustomerReportsPage } from './features/customer-reports/CustomerReportsPage.js';
import { MailSettingsPage } from './features/mail-settings/MailSettingsPage.js';
import { ForecastPage } from './features/forecast/ForecastPage.js';
import { DashboardsPage } from './features/dashboards/DashboardsPage.js';
import { DashboardDetailPage } from './features/dashboards/DashboardDetailPage.js';
import { InsightsStudioPage } from './features/insights-studio/InsightsStudioPage.js';
import { NotificationsPage } from './features/notifications/NotificationsPage.js';
import { GraphSettingsPage } from './features/graph/GraphSettingsPage.js';
import { CpqIntegrationPage } from './features/cpq/CpqIntegrationPage.js';
import { AssistantSettingsPage } from './features/assistant/AssistantSettingsPage.js';
import { CatalystPortalPage } from './features/catalyst-portal/CatalystPortalPage.js';
import { OnboardingPage } from './features/onboarding/OnboardingPage.js';
import { QuotesPage } from './features/quotes/QuotesPage.js';
import { QuoteDetailPage } from './features/quotes/QuoteDetailPage.js';
import { QuoteTemplatesPage } from './features/quotes/QuoteTemplatesPage.js';
import { QuoteTemplateDetailPage } from './features/quotes/QuoteTemplateDetailPage.js';
import { ClickupPage } from './features/clickup/ClickupPage.js';
import { MailerlitePage } from './features/mailerlite/MailerlitePage.js';
import { MaintenancePage } from './features/maintenance/MaintenancePage.js';
import { SnapshotManagementPage } from './features/snapshot/SnapshotManagementPage.js';
import { HomeRoute } from './pages/HomeRoute.js';
import { ComingSoonPage } from './pages/ComingSoonPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        <Route
          element={
            <AuthProvider>
              <AppLayout />
            </AuthProvider>
          }
        >
          <Route path="/" element={<HomeRoute />} />
          <Route path="/sales/prospects" element={<ProspectsPage />} />
          <Route path="/sales/prospects/:id" element={<ProspectsPage />} />
          <Route path="/sales/opportunities" element={<OpportunitiesPage />} />
          <Route path="/sales/opportunities/:id" element={<OpportunitiesPage />} />
          <Route path="/sales/quotes" element={<QuotesPage />}>
            <Route path=":id" element={<QuoteDetailPage />} />
          </Route>
          <Route path="/sales/onboarding" element={<OnboardingPage />} />
          <Route path="/sales/onboarding/:id" element={<OnboardingPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomersPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/delivery/work-forecast" element={<WorkForecastPage />} />
          <Route path="/projects" element={<ProjectsPage />}>
            <Route path=":id" element={<ProjectDetailPage />} />
          </Route>
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/:id" element={<TicketsPage />} />
          <Route path="/time-entries" element={<TimeEntriesPage />} />
          <Route path="/time-entries/:id" element={<TimeEntriesPage />} />
          <Route path="/timesheets" element={<TimesheetsPage />}>
            <Route path=":id" element={<TimesheetDetailPage />} />
          </Route>
          <Route path="/approvals" element={<ApprovalsPage />}>
            <Route path=":id" element={<TimesheetDetailPage />} />
          </Route>
          <Route path="/agreements" element={<AgreementsPage />}>
            <Route path=":id" element={<AgreementDetailPage />} />
          </Route>
          <Route path="/invoices" element={<InvoicesPage />}>
            <Route path=":id" element={<InvoiceDetailPage />} />
          </Route>
          <Route path="/invoices/generate" element={<GenerateInvoicesPage />} />
          <Route path="/invoices/qbo-batch" element={<QboBatchPage />} />
          <Route path="/billing-audit" element={<BillingAuditPage />} />{' '}
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/service-categories" element={<ServiceCategoriesPage />} />
          <Route path="/billing-classes" element={<BillingClassesPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/roles" element={<RolesPage />} />
          <Route path="/api-users" element={<ApiUsersPage />} />
          <Route path="/integrations/excel-access" element={<ExcelAccessPage />} />
          <Route path="/admin/ticket-types" element={<TicketTypesPage />} />
          <Route path="/admin/ticket-type-groups" element={<TicketTypeGroupsPage />} />
          <Route path="/admin/ticket-statuses" element={<TicketStatusesPage />} />
          <Route path="/admin/customer-types" element={<CustomerTypesPage />} />
          <Route path="/admin/agreement-subtypes" element={<AgreementSubtypesPage />} />
          <Route path="/admin/invoice-templates" element={<InvoiceTemplatesPage />} />
          <Route path="/admin/quote-templates" element={<QuoteTemplatesPage />} />
          <Route path="/admin/quote-templates/:id" element={<QuoteTemplateDetailPage />} />
          <Route path="/admin/email-settings" element={<MailSettingsPage />} />
          <Route path="/admin/microsoft-graph" element={<GraphSettingsPage />} />
          <Route path="/admin/maintenance" element={<MaintenancePage />} />
          <Route path="/admin/snapshot" element={<SnapshotManagementPage />} />
          <Route path="/integrations/qbo" element={<QboIntegrationPage />} />
          <Route path="/integrations/connectwise" element={<ConnectWisePage />} />
          <Route path="/integrations/clickup" element={<ClickupPage />} />
          <Route path="/integrations/mailerlite" element={<MailerlitePage />} />
          <Route path="/integrations/catalyst-portal" element={<CatalystPortalPage />} />
          <Route path="/integrations/connectwise-cpq" element={<CpqIntegrationPage />} />
          <Route path="/admin/ai-assistant" element={<AssistantSettingsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/bonus" element={<BonusReportPage />} />
          <Route path="/customer-reports" element={<CustomerReportsPage />} />
          <Route path="/insights/forecast" element={<ForecastPage />} />
          <Route path="/insights/studio" element={<InsightsStudioPage />} />
          <Route path="/dashboards" element={<DashboardsPage />} />
          <Route path="/dashboards/:id" element={<DashboardDetailPage />} />
          <Route path="/insights/notifications" element={<NotificationsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
